<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { IWidgetPreview, ICreateWidget } from '../../services/api/widget/types';
    import DonutChart from '../widgets/chartTypes/DonutChart.vue';
    import ColumnChart from '../widgets/chartTypes/ColumnChart.vue';
    import LineChart from '../widgets/chartTypes/LineChart.vue';
    import PieChart from '../widgets/chartTypes/PieChart.vue';
    import PolarChart from '../widgets/chartTypes/PolarChart.vue';

    const props = defineProps<{
        preview: IWidgetPreview;
        widget: ICreateWidget<'Graph'>;
    }>();

    const hasData = computed(() => {
        const { graphType } = props.widget;
        if (graphType === 'Donut') {
            const chart = props.preview.donutChart;
            if (!chart) return null;
            return chart.value !== null;
        }
        if (graphType === 'Column') {
            const chart = props.preview.columnChart;
            if (!chart) return null;
            return Array.isArray(chart.data) && chart.data.length > 0;
        }
        if (graphType === 'Line') {
            const chart = props.preview.lineChart;
            if (!chart) return null;
            return Array.isArray(chart.data) && chart.data.length > 0;
        }
        if (graphType === 'Pie') {
            const chart = props.preview.pieChart;
            if (!chart) return null;
            return Array.isArray(chart.data) && chart.data.length > 0;
        }
        if (graphType === 'Polar') {
            const chart = props.preview.polarChart;
            if (!chart) return null;
            return Array.isArray(chart.series) && chart.series.length > 0;
        }
        return null;
    });
</script>

<template>
    <div class="relative h-full w-full">
        <template v-if="hasData === true">
            <DonutChart
                v-if="widget.graphType === 'Donut' && preview.donutChart"
                :data="preview.donutChart"
            />
            <ColumnChart
                v-else-if="widget.graphType === 'Column' && preview.columnChart"
                :data="preview.columnChart"
            />
            <LineChart
                v-else-if="widget.graphType === 'Line' && preview.lineChart"
                :data="preview.lineChart"
            />
            <PieChart
                v-else-if="widget.graphType === 'Pie' && preview.pieChart"
                :data="preview.pieChart"
            />
            <PolarChart
                v-else-if="widget.graphType === 'Polar' && preview.polarChart"
                :data="preview.polarChart"
            />
        </template>
        <div v-else-if="hasData === false" class="flex items-center justify-center h-full p-8">
            <p class="text-body text-gray-dark text-center">
                {{ __('widget-form-preview-no-data') }}
            </p>
        </div>
    </div>
</template>
